import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCandidateById,
  updateCandidate,
  clearCandidateError,
  clearSelectedCandidate,
} from "../slice/candidateSlice";
import { useDialog } from "../components/ConfirmDialog";
import useParseAndFill from "../hooks/useParseAndFill";

// This page is Edit-only — adding a new candidate happens inline on the
// Candidates dashboard now (matching how clients are added), the same way
// there's never been a separate "Add Client" page.
const WORK_AUTH_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "US_CITIZEN", label: "US Citizen" },
  { value: "GREEN_CARD", label: "Green Card" },
  { value: "H1B", label: "H1B" },
  { value: "OPT", label: "OPT" },
  { value: "CPT", label: "CPT" },
  { value: "TN_VISA", label: "TN Visa" },
  { value: "OTHER", label: "Other" },
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  currentCompany: "",
  currentTitle: "",
  totalExperience: "",
  workAuthorization: "",
  education: "",
  certifications: "",
  skillCategories: "",
};

// Turns the candidate's stored skillCategories ([{ label, skills }]) into
// the editable "Label: skill1, skill2" per-line text the textarea shows —
// the same grouped shape the original resume used, not a flat list.
// Falls back to a single "Skills: ..." line for older candidates that only
// have the flat `skills` array (no category structure captured yet).
function formatSkillCategoriesText(skillCategories, flatSkills) {
  if (skillCategories && skillCategories.length) {
    return skillCategories.map((c) => `${c.label}: ${(c.skills || []).join(", ")}`).join("\n");
  }
  if (flatSkills && flatSkills.length) {
    return `Skills: ${flatSkills.join(", ")}`;
  }
  return "";
}

// Parses the textarea back into [{ label, skills }]. Each line is
// "Label: skill1, skill2, ..."; a line with no colon is treated as
// uncategorized skills and grouped under "Additional Skills" rather than
// dropped.
function parseSkillCategoriesText(text) {
  return (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const colonIdx = line.indexOf(":");
      if (colonIdx === -1) {
        return { label: "Additional Skills", skills: line.split(",").map((s) => s.trim()).filter(Boolean) };
      }
      return {
        label: line.slice(0, colonIdx).trim(),
        skills: line.slice(colonIdx + 1).split(",").map((s) => s.trim()).filter(Boolean),
      };
    })
    .filter((c) => c.label && c.skills.length > 0);
}

// Builds the form's values from a saved candidate. Used both to load the
// form and to detect unsaved edits (by comparing the current form against
// what's saved). totalExperience is kept as a string so a typed "5" and a
// saved 5 compare as equal.
function candidateToForm(c) {
  return {
    name: c.name || "",
    email: c.email || "",
    phone: c.phone || "",
    currentCompany: c.currentCompany || "",
    currentTitle: c.currentTitle || "",
    totalExperience: c.totalExperience != null ? String(c.totalExperience) : "",
    workAuthorization: c.workAuthorization || "",
    education: (c.education || []).join("\n"),
    certifications: (c.certifications || []).join("\n"),
    skillCategories: formatSkillCategoriesText(c.skillCategories, c.skills),
  };
}

export default function CandidateForm() {
  const { id } = useParams();

  const [form, setForm] = useState(emptyForm);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedCandidate, resumeHistory, status, error } = useSelector((state) => state.candidates);
  const loading = status === "loading";
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const { dialogElement, notify, confirm } = useDialog();
  const { runParseAndFill, workingVersionId } = useParseAndFill({ candidateId: id, notify, confirm });

  // Reset to empty FIRST, before the fetch resolves — otherwise if this
  // component is reused for a different candidate id, the previous
  // candidate's values could flash or linger until the new data arrives.
  useEffect(() => {
    setForm(emptyForm);
    dispatch(fetchCandidateById(id));
    return () => {
      dispatch(clearSelectedCandidate());
      dispatch(clearCandidateError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (selectedCandidate && selectedCandidate._id === id) {
      setForm(candidateToForm(selectedCandidate));
    }
  }, [selectedCandidate, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearCandidateError());

    const data = new FormData();
    // totalExperience needs special handling — an empty string would fail
    // Mongoose's Number cast on save, so we simply omit it when blank
    // instead of sending "". skillCategories needs its own handling too —
    // FormData fields are always strings, so the parsed [{ label, skills }]
    // structure goes over as JSON, not as the raw textarea text.
    Object.entries(form).forEach(([key, value]) => {
      if (key === "totalExperience" && value === "") return;
      if (key === "skillCategories") {
        data.append("skillCategories", JSON.stringify(parseSkillCategoriesText(value)));
        return;
      }
      data.append(key, value);
    });

    const result = await dispatch(updateCandidate({ id, formData: data }));
    if (updateCandidate.fulfilled.match(result)) {
      navigate("/dashboard");
    }
  };

  // The candidate's current master resume, if one's been uploaded — needed
  // to know whether to parse it first or just apply already-parsed data.
  const latestMaster = (resumeHistory || []).find((rv) => rv.isLatestMaster);

  // True when the form has edits that haven't been saved yet.
  const hasUnsavedChanges =
    !!selectedCandidate &&
    selectedCandidate._id === id &&
    JSON.stringify(form) !== JSON.stringify(candidateToForm(selectedCandidate));

  // Same single action as the Candidate Detail page (shared hook): parses
  // the latest master resume if it hasn't been parsed yet, then fills the
  // profile. Filling saves straight to the database and reloads this form,
  // so any unsaved typing would be lost — warn about that first.
  const handleFillFromResume = async () => {
    if (!latestMaster) return;

    if (hasUnsavedChanges) {
      const proceed = await confirm(
        "You have unsaved changes on this form. Filling from the resume reloads the form from the saved profile, so those changes will be lost. Continue?",
        { title: "Unsaved changes", confirmLabel: "Fill anyway" }
      );
      if (!proceed) return;
    }

    const updated = await runParseAndFill(latestMaster, { overwrite: overwriteExisting });
    if (updated) {
      await notify(
        overwriteExisting
          ? "The form was filled from the resume, replacing existing values. Review the fields below."
          : "Blank fields were filled from the resume. Review the fields below.",
        { title: "Form filled" }
      );
    }
  };

  const isFilling = !!latestMaster && workingVersionId === latestMaster.id;

  return (
    <div className="page-container" style={{width:"75%"}}>
      <div className="page-header">
        <h2>Edit Candidate</h2>
      </div>

      <p style={{ color: "#5a5a5a", fontSize: "14px" }}>
        To upload a new master resume version or view full resume history, go to{" "}
        <Link to={`/dashboard/candidates/${id}`}>this candidate's detail page</Link>.
      </p>

      {error && <div className="auth-error">{error}</div>}

      <div style={{ margin: "8px 0 20px", padding: "12px", backgroundColor: "#f4f5f7", borderRadius: "4px" }}>
        <strong style={{ fontSize: "14px" }}>Fill Profile from Resume</strong>
        <p style={{ fontSize: "13px", color: "#5a5a5a", margin: "4px 0 8px" }}>
          {latestMaster
            ? latestMaster.parsedData
              ? "This candidate's latest master resume has already been read by AI — filling won't call the AI again."
              : "Reads the latest master resume with AI, then fills in the fields below in one step."
            : "No master resume on file yet — upload one from this candidate's detail page first."}
        </p>
        {latestMaster && (
          <>
            <label style={{ fontSize: "13px", display: "block", marginBottom: "8px" }}>
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
                style={{ marginRight: "6px" }}
              />
              Overwrite fields that already have a value (default: only fills in blanks)
            </label>
            <button type="button" onClick={handleFillFromResume} disabled={isFilling}>
              {isFilling
                ? latestMaster.parsedData
                  ? "Filling..."
                  : "Parsing & Filling..."
                : "Fill Profile from Resume"}
            </button>
          </>
        )}
      </div>

      <form className="candidate-form candidate-form-wide" onSubmit={handleSubmit}>
        <div className="form-section">
          <p className="form-section-title">Basic Information</p>
          <div className="form-grid form-grid-3">
            <div className="form-field">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. John Smith"
                required
              />
            </div>

            <div className="form-field">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="e.g. john.smith@example.com"
                required
              />
            </div>

            <div className="form-field">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="e.g. +1 555 123 4567"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <p className="form-section-title">Professional Details</p>
          <div className="form-grid form-grid-4">
            <div className="form-field">
              <label>Current Company</label>
              <input type="text" name="currentCompany" value={form.currentCompany} onChange={handleChange} />
            </div>

            <div className="form-field">
              <label>Current Title</label>
              <input type="text" name="currentTitle" value={form.currentTitle} onChange={handleChange} />
            </div>

            <div className="form-field">
              <label>Total Experience (years)</label>
              <input
                type="number"
                name="totalExperience"
                value={form.totalExperience}
                onChange={handleChange}
                min="0"
                step="0.5"
              />
            </div>

            <div className="form-field">
              <label>Work Authorization</label>
              <select name="workAuthorization" value={form.workAuthorization} onChange={handleChange}>
                {WORK_AUTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <p className="form-section-title">Skills &amp; Background</p>
          <div className="form-grid">
            <div className="form-field form-field-full">
              <label>Skills (grouped, one category per line: "Category: skill1, skill2, ...")</label>
              <textarea
                name="skillCategories"
                rows={8}
                value={form.skillCategories}
                onChange={handleChange}
                placeholder={"Programming: Python, SQL, Bash\nCloud & Data Platforms: AWS, GCP, Snowflake"}
              />
            </div>

            <div className="form-field form-field-full">
              <label>Education (one per line)</label>
              <textarea
                name="education"
                rows={3}
                value={form.education}
                onChange={handleChange}
                placeholder="B.Tech Computer Science - XYZ University, 2018"
              />
            </div>

            <div className="form-field form-field-full">
              <label>Certifications (one per line)</label>
              <textarea
                name="certifications"
                rows={3}
                value={form.certifications}
                onChange={handleChange}
                placeholder="AWS Certified Solutions Architect - 2023"
              />
            </div>
          </div>
        </div>

        <button type="submit" disabled={loading} style={{ marginTop: "28px" }}>
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </form>
      {dialogElement}
    </div>
  );
}
